import { useCallback, useEffect, useRef, useState } from 'react';

/*
  THE SIGN-IN SCENE, mounted, rebrand step 7, 17 September 2026.

  This is the seam between the sign-in page and the conveyor
  (`conveyorScene.js`). What it guarantees, which is the guide's 9.1 and the
  step's own rule: **the 3D never slows signing in.**

  - **The library is fetched after the page is on screen**, with a dynamic
    import, so the form is usable while it loads and it is not in the main
    bundle at all.
  - **If it fails to load, or the browser cannot do WebGL, the card simply
    shows** over the still picture of the warehouse (`public/signin/`, since 18
    September 2026) and everything still works (guide 9.9).
  - **Reduced motion**: the scene is drawn once and never animated, the card is
    there from the start, and nothing moves (guide 9.9).
  - **Leaving the page disposes of the scene**: geometries, materials,
    textures and the renderer.
  - The page's own light or dark comes from the device, never from the person's
    theme (guide 9.1).
*/

/* How long the card waits for the box before it comes on its own (owner, 18 September 2026). */
export const CARD_WAIT_MS = 4000;

const DARK_QUERY = '(prefers-color-scheme: dark)';
const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

const asks = query => {
  try { return !!(window.matchMedia && window.matchMedia(query).matches); } catch { return false; }
};

export default function useConveyor({ pageRef, cardRef, canvasRef, onSuccessBoxClosed }) {
  const sceneRef = useRef(null);
  // loading: the card is hidden and waiting for the box. running: the scene is
  // up and hides or shows the card itself. late: the wait ran out, the card
  // shows, and the scene may still arrive (without its intro). off: no scene.
  const [state, setState] = useState('loading');
  const closedRef = useRef(onSuccessBoxClosed);
  closedRef.current = onSuccessBoxClosed;

  useEffect(() => {
    let alive = true;
    let frame = 0;
    let scene = null;
    let shownEarly = false;
    const reduceMotion = asks(REDUCE_QUERY);

    /*
      **The card waits up to four seconds for the box, and no longer.** If the
      scene has not started by then, the card is shown and the intro is given
      up: a person on a slow connection gets a sign-in form, not an empty
      middle of the screen.

      It was 1.2 seconds until 18 September 2026, and on a real connection
      that is less than the library takes to arrive and build, so the card
      nearly always turned up on its own and the box never brought it. The
      owner chose four: *"Up to 4 seconds"*. The still picture of the
      warehouse (`public/signin/`) is on screen for the wait, so it is a
      picture of the scene rather than an empty page.

      **Shown through React's state, never by editing the page's class by
      hand**: React owns `className` and puts back whatever it last rendered.
      See `cardVisibility.js`.
    */
    const failsafe = window.setTimeout(() => {
      if (!alive || sceneRef.current) return;
      shownEarly = true;
      setState('late');
    }, CARD_WAIT_MS);

    (async () => {
      try {
        const THREE = await import('three');
        if (!alive || !canvasRef.current || !cardRef.current || !pageRef.current) return;
        const { createConveyorScene } = await import('./conveyorScene');
        if (!alive || !canvasRef.current) return;
        scene = createConveyorScene({
          THREE,
          canvas: canvasRef.current,
          card: cardRef.current,
          pageEl: pageRef.current,
          mode: asks(DARK_QUERY) ? 'dark' : 'light',
          reduceMotion,
          onSuccessBoxClosed: () => closedRef.current && closedRef.current(),
        });
        sceneRef.current = scene;
        window.clearTimeout(failsafe);
        setState('running');
        scene.start({ skipIntro: shownEarly });
        scene.render();
        if (!reduceMotion) {
          const clock = new THREE.Clock();
          const tick = () => {
            if (!alive) return;
            const dt = Math.min(0.05, clock.getDelta());
            scene.update(dt);
            scene.render();
            frame = window.requestAnimationFrame(tick);
          };
          frame = window.requestAnimationFrame(tick);
        }
      } catch (err) {
        /*
          **The card shows anyway** (guide 9.9). A browser with no WebGL, a
          blocked file, a slow network: none of them is a reason a person cannot
          sign in, and none of them gets an error in front of them.
        */
        if (!alive) return;
        window.clearTimeout(failsafe);
        setState('off');
      }
    })();

    const onResize = () => { if (scene) { scene.resize(); scene.render(); } };
    window.addEventListener('resize', onResize);

    return () => {
      alive = false;
      window.clearTimeout(failsafe);
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      if (scene) scene.dispose();
      sceneRef.current = null;
    };
  }, [pageRef, cardRef, canvasRef]);

  /* What the page asks the scene to do. Each one is safe with no scene at all. */
  const success = useCallback(isReady => {
    const s = sceneRef.current;
    if (!s) { if (closedRef.current) closedRef.current(); return; }
    s.success(isReady);
  }, []);
  const cancelSuccess = useCallback(() => { const s = sceneRef.current; if (s) s.cancelSuccess(); }, []);
  const jam = useCallback(() => { const s = sceneRef.current; if (s) s.jam(); }, []);
  const setTyping = useCallback(v => { const s = sceneRef.current; if (s) s.setTyping(v); }, []);

  return { state, success, cancelSuccess, jam, setTyping };
}
